import { useNavigate } from 'react-router-dom';
import MainNav from '../components/MainNav';
import MainFooter from '../components/MainFooter';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, MessageSquare, Calendar, Star, ArrowRight, Heart } from 'lucide-react';

const CommunityConnectPage = () => {
    const navigate = useNavigate();

    const features = [
        {
            icon: Users,
            title: 'Member Directory',
            description: 'Connect with fellow brothers who share your interests and experiences.'
        },
        {
            icon: MessageSquare,
            title: 'Private Messaging',
            description: 'Build meaningful connections through secure peer-to-peer messaging.'
        },
        {
            icon: Calendar,
            title: 'Share Events',
            description: 'Discover and share community events, gatherings, and opportunities.'
        },
        {
            icon: Star,
            title: 'Brother Spotlights',
            description: 'Celebrate the achievements and stories of members in our community.'
        }
    ];

    return (
        <div className="min-h-screen bg-[#3D4435]">
            <MainNav />
            
            <main className="pt-32 pb-24 px-6">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-4">
                            In The Community
                        </p>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Playfair_Display'] mb-6">
                            Community
                        </h1>
                        <p className="text-[#A0A0A0] text-lg max-w-2xl mx-auto">
                            Join our growing brotherhood and connect with like-minded men on a journey of wellness and growth
                        </p>
                    </div>

                    {/* Join CTA */}
                    <Card className="bg-gradient-to-br from-[#4A5240] to-[#3D4435] border-[#C9A227]/30 mb-16">
                        <CardContent className="p-8 lg:p-12">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                                <div>
                                    <div className="w-16 h-16 rounded-full bg-[#C9A227]/10 flex items-center justify-center mb-6">
                                        <Heart className="w-8 h-8 text-[#C9A227]" />
                                    </div>
                                    <h2 className="text-3xl font-bold text-white font-['Playfair_Display'] mb-4">
                                        Become Part of the Brotherhood
                                    </h2>
                                    <p className="text-[#A0A0A0] mb-6 leading-relaxed">
                                        Create your member profile to access the full community experience. Connect with brothers, share your story, and grow together in a supportive environment.
                                    </p>
                                    <div className="flex flex-wrap gap-4">
                                        <Button
                                            size="lg"
                                            onClick={() => navigate('/member/register')}
                                            className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold"
                                            data-testid="join-community-btn"
                                        >
                                            Create Your Account
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </Button>
                                        <Button
                                            size="lg"
                                            variant="outline"
                                            onClick={() => navigate('/member/login')}
                                            className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white"
                                        >
                                            Member Login
                                        </Button>
                                    </div>
                                </div>
                                <div className="hidden lg:block">
                                    <img 
                                        src="https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/cgrfsyob_image.png"
                                        alt="Recovery Room Brothers"
                                        className="rounded-2xl w-full object-cover"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Features */}
                    <section className="mb-16">
                        <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-8 text-center">
                            Member Portal Features
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {features.map((feature, idx) => (
                                <Card key={idx} className="bg-[#4A5240] border-[#5A6350] hover:border-[#C9A227]/50 transition-all">
                                    <CardContent className="p-6 text-center">
                                        <div className="w-14 h-14 rounded-xl bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-4">
                                            <feature.icon className="w-7 h-7 text-[#C9A227]" />
                                        </div>
                                        <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
                                        <p className="text-[#A0A0A0] text-sm">{feature.description}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </section>

                    {/* Testimonial/Quote */}
                    <Card className="bg-[#4A5240] border-[#5A6350]">
                        <CardContent className="p-8 lg:p-12 text-center">
                            <p className="text-2xl text-white font-['Playfair_Display'] italic leading-relaxed mb-6">
                                "Brotherhood. Growth. Purpose. These aren't just words—they're the foundation of everything we build together at Recovery Room."
                            </p>
                            <p className="text-[#C9A227] font-medium">— Recovery Room Community</p>
                        </CardContent>
                    </Card>
                </div>
            </main>

            <MainFooter />
        </div>
    );
};

export default CommunityConnectPage;
